import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { AlertTriangle, Lock } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
// Re-poll the summary periodically so a freshly-resolved dispute clears the
// banner on the rep's screen without a page refresh. 60s is a good balance
// between freshness and chatter — disputes don't change every second.
const POLL_MS = 60 * 1000;

/**
 * Red, bold global banner that warns the current sales rep that one or
 * more of their loads is in dispute. After {block_threshold_days} days
 * (15 by default) the banner switches to a "BLOCKED" state and the
 * backend simultaneously rejects new-load creation.
 *
 * Renders nothing for non-sales roles or when count === 0 — keep the
 * navbar quiet otherwise.
 *
 * Note: the disputed-loads list itself is intentionally NOT shown inline
 * here — it would clutter every page. Instead the banner links to the
 * rep's own My Loads view filtered to disputes.
 */
export function DisputeBanner() {
  const { user, token } = useAuth();
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    if (!user || !token || user.role !== 'sales') return;
    let cancelled = false;
    const fetchSummary = async () => {
      try {
        const res = await axios.get(`${API}/loads/disputes/my-summary`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) setSummary(res.data);
      } catch {
        // 401s already handled globally by axios interceptor; ignore.
      }
    };
    fetchSummary();
    const iv = setInterval(fetchSummary, POLL_MS);
    return () => { cancelled = true; clearInterval(iv); };
  }, [user, token]);

  if (!user || user.role !== 'sales') return null;
  if (!summary || summary.count <= 0) return null;

  const graceActive = !!summary.grace_active;
  const blocked = !!summary.blocking;  // backend already factors in grace
  const days = summary.oldest_days || 0;
  const count = summary.count;

  // When grace is active AND we'd otherwise be in the BLOCKED state, switch
  // the whole banner to a green "grace active" message. The top-bar chip
  // shows the live countdown.
  if (graceActive && days >= (summary.block_threshold_days || 15)) {
    return (
      <div
        className="w-full bg-emerald-600 text-white border-b border-emerald-800 px-4 py-2.5 shadow"
        role="alert"
        aria-live="polite"
        data-testid="dispute-banner-grace"
      >
        <div className="flex items-center justify-between gap-3 max-w-screen-2xl mx-auto">
          <div className="flex items-center gap-2.5 font-bold text-sm sm:text-base">
            <Lock className="h-5 w-5 shrink-0" />
            <span>
              GRACE PERIOD ACTIVE — Load creation is temporarily unblocked.
              See the timer in the top-right for how much time remains.
            </span>
          </div>
          <a
            href="/loads?dispute=1"
            className="hidden sm:inline-block text-xs underline whitespace-nowrap hover:text-emerald-100"
            data-testid="dispute-banner-link"
          >
            View Disputed Loads →
          </a>
        </div>
      </div>
    );
  }

  return (
    <div
      className={
        blocked
          ? 'w-full bg-red-700 text-white border-b-2 border-red-900 px-4 py-2.5 shadow-md'
          : 'w-full bg-red-600 text-white border-b border-red-800 px-4 py-2.5 shadow'
      }
      role="alert"
      aria-live="polite"
      data-testid="dispute-banner"
    >
      <div className="flex items-center justify-between gap-3 max-w-screen-2xl mx-auto">
        <div className="flex items-center gap-2.5 font-bold text-sm sm:text-base">
          {blocked ? <Lock className="h-5 w-5 shrink-0" /> : <AlertTriangle className="h-5 w-5 shrink-0" />}
          <span>
            {blocked ? 'LOAD CREATION BLOCKED — ' : 'DISPUTE WARNING — '}
            You have <span className="underline">{count}</span> load{count === 1 ? '' : 's'} in dispute
            {days > 0 && <> for <span className="underline">{days}</span> day{days === 1 ? '' : 's'}</>}.
            {blocked
              ? ` You cannot create new loads until accounting resolves them.`
              : ` Resolve before ${Math.max(1, (summary.block_threshold_days || 15) - days)} day${(summary.block_threshold_days || 15) - days === 1 ? '' : 's'} or you will be blocked from creating new loads.`}
          </span>
        </div>
        <a
          href="/loads?dispute=1"
          className="hidden sm:inline-block text-xs underline whitespace-nowrap hover:text-red-100"
          data-testid="dispute-banner-link"
        >
          View Disputed Loads →
        </a>
      </div>
    </div>
  );
}

export default DisputeBanner;
